import * as Passport from "../utils/Passport.js";
import MapExploration from "../utils/MapExploration.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import { countryPath } from "../utils/SlugUtils.js";
import { PICTO_FLAG, PICTO_CAPITAL, PICTO_MAP } from "../utils/Pictos.js";

/** Zones, dans l'ordre du sélecteur de Map Nerd. */
const ZONES = ["af", "eu", "as", "am"];

const escapeHtml = (s) => String(s)
	.replace(/&/g, "&amp;")
	.replace(/</g, "&lt;")
	.replace(/>/g, "&gt;")
	.replace(/"/g, "&quot;");

/**
 * Page /passeport (/en/passport) : la collection des tampons (49 du TODO).
 * Bilan, filtres (zone, à compléter), puis un carnet de 193 pays où chaque
 * case montre ses trois tampons et mène à la fiche du pays.
 */
export default class PassportPage {
	constructor() {
		this.page = document.querySelector(".page.passport");
		if (!this.page) return;
		this.app = window.geoNerdApp;
		this.lang = this.app.languageManager;
		this.t = (k) => this.lang.t(k);
		this.zone = "all";
		this.onlyMissing = false;
		this.slugs = {};

		this.summaryEl = this.page.querySelector(".passport-summary");
		this.filtersEl = this.page.querySelector(".passport-filters");
		this.gridEl = this.page.querySelector(".passport-grid");

		this.render();
		// Les fiches pays : la table des slugs n'est chargée qu'ici, la grille
		// s'affiche sans attendre et ses cases deviennent des liens à l'arrivée.
		fetch("/data/country-slugs.json")
			.then(r => (r.ok ? r.json() : {}))
			.then(slugs => {
				this.slugs = slugs;
				this.renderGrid();
			})
			.catch(() => {});
	}

	countries() {
		const locale = this.lang.currentLang === "en" ? "en" : "fr";
		return [...this.app.countries].sort((a, b) => a.name.localeCompare(b.name, locale));
	}

	mapCodes() {
		return new Set((this.app.easyCountries || []).map(c => c.code));
	}

	render() {
		this.renderSummary();
		this.renderFilters();
		this.renderGrid();
	}

	renderSummary() {
		const codes = this.app.countries.map(c => c.code);
		const s = Passport.summary(codes, this.mapCodes());
		const bar = (label, value, total, icon) => `
			<div class="passport-bar">
				<span class="passport-bar-label"><span aria-hidden="true">${icon}</span> ${label}</span>
				<span class="passport-bar-track"><span class="passport-bar-fill" style="width: ${total ? Math.round((value / total) * 100) : 0}%"></span></span>
				<span class="passport-bar-value">${value}/${total}</span>
			</div>`;
		this.summaryEl.innerHTML = `
			<p class="passport-complete"><strong>${s.complete}</strong><span>/${s.total}</span></p>
			<p class="passport-complete-label">${this.t("passport.completeLabel")}</p>
			${bar(this.t("passport.flags"), s.flag, s.total, PICTO_FLAG)}
			${bar(this.t("passport.capitals"), s.capital, s.total, PICTO_CAPITAL)}
			${bar(this.t("passport.maps"), s.map, s.mapTotal, PICTO_MAP)}`;
	}

	renderFilters() {
		const zoneButton = (zone) => {
			const label = zone === "all" ? this.t("passport.allZones") : this.t(`mapExploration.continents.${zone}`);
			return `<button type="button" class="passport-filter${this.zone === zone ? " active" : ""}" data-zone="${zone}">${label}</button>`;
		};
		this.filtersEl.innerHTML = `
			<div class="passport-zones">${["all", ...ZONES].map(zoneButton).join("")}</div>
			<label class="passport-missing"><input type="checkbox"${this.onlyMissing ? " checked" : ""}> ${this.t("passport.onlyMissing")}</label>`;
		this.filtersEl.querySelectorAll(".passport-filter").forEach(btn => btn.addEventListener("click", () => {
			this.zone = btn.dataset.zone;
			this.renderFilters();
			this.renderGrid();
		}));
		this.filtersEl.querySelector(".passport-missing input").addEventListener("change", (e) => {
			this.onlyMissing = e.target.checked;
			this.renderGrid();
		});
	}

	renderGrid() {
		const passport = Passport.read();
		const discovered = MapExploration.getDiscovered();
		const mapCodes = this.mapCodes();
		const continents = this.app.continents || {};
		const lang = this.lang.currentLang === "en" ? "en" : "fr";
		const prefix = lang === "en" ? "/en" : "";

		const tiles = this.countries().map(country => {
			if (this.zone !== "all" && continents[country.code] !== this.zone) return "";
			const bits = Passport.stampsOf(country.code, passport, discovered);
			const onMap = mapCodes.has(country.code);
			const needed = Passport.STAMPS.flag | Passport.STAMPS.capital | (onMap ? Passport.STAMPS.map : 0);
			const complete = (bits & needed) === needed;
			if (this.onlyMissing && complete) return "";

			const stamp = (bit, icon, label) => {
				const state = this.t(bits & bit ? "passport.earned" : "passport.missing");
				return `<span class="stamp${bits & bit ? " is-on" : ""}" role="img" title="${label}" aria-label="${label} : ${state}">${icon}</span>`;
			};
			const mapStamp = onMap
				? stamp(Passport.STAMPS.map, PICTO_MAP, this.t("passport.map"))
				: `<span class="stamp is-na" title="${this.t("passport.notOnMap")}" aria-label="${this.t("passport.notOnMap")}">·</span>`;
			const slug = this.slugs[country.code]?.[lang];
			const tag = slug ? "a" : "div";
			const href = slug ? ` href="${prefix}/${countryPath(lang, slug)}"` : "";
			return `
				<${tag} class="passport-tile${complete ? " is-complete" : ""}"${href}>
					<img src="${getFlagPath(country.code)}" alt="" width="48" height="32" loading="lazy" decoding="async">
					<span class="passport-name">${escapeHtml(country.name)}</span>
					<span class="passport-stamps">
						${stamp(Passport.STAMPS.flag, PICTO_FLAG, this.t("passport.flag"))}
						${stamp(Passport.STAMPS.capital, PICTO_CAPITAL, this.t("passport.capital"))}
						${mapStamp}
					</span>
				</${tag}>`;
		}).join("");

		this.gridEl.innerHTML = tiles || `<p class="passport-empty">${this.t("passport.allDone")}</p>`;
	}
}
